import { useCallback, useEffect, useState } from 'react';
import { FileStack, Printer } from '../../lib/icons';
import { toast } from 'sonner';
import {
  fetchSousProduits,
  fetchContactsRoles,
  fetchFacturesLibres,
  facturerVoyagesGroupes,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { ContactCombobox } from '../shared/ContactCombobox';
import { PrintDoc } from '../print/PrintDoc';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

export const FacturationSousProduits = ({ onDone }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [sp, setSp] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [factures, setFactures] = useState([]);
  const [sel, setSel] = useState({ grignons: [], noyaux: [] });
  const [f, setF] = useState({ date: aujourdhui, tiersId: '', notes: '' });
  const [apercu, setApercu] = useState(null);

  const load = useCallback(async () => {
    setSp(await fetchSousProduits());
    // Tous les contacts (clients, industriels, transporteurs) — hors personnel.
    setTiers((await fetchContactsRoles()).filter((c) => c.type !== 'personnel'));
    setFactures(await fetchFacturesLibres());
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!sp) return null;

  const voyagesGr = sp.grignons.filter((g) => g.statut === 'Pesé');
  const voyagesNo = sp.noyaux.filter((n) => !n.facture);

  const normNom = (v) => String(v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

  // Coche + auto-remplissage du client à facturer d'après l'acheteur du voyage.
  const cocher = (cle, ligne) => {
    const id = ligne.id;
    const dejaCoche = sel[cle].includes(id);
    setSel((s) => ({
      ...s,
      [cle]: dejaCoche ? s[cle].filter((x) => x !== id) : [...s[cle], id],
    }));
    if (dejaCoche) return;
    const contact = tiers.find((t) => normNom(t.nom) === normNom(ligne.acheteur));
    if (contact) setF((p) => ({ ...p, tiersId: contact.id }));
    else toast.error(`« ${ligne.acheteur} » n'existe pas dans les contacts — sélectionnez le client manuellement.`);
  };

  const total =
    voyagesGr.filter((g) => sel.grignons.includes(g.id)).reduce((s, g) => s + (g.tonnage || 0) * g.prix, 0) +
    voyagesNo.filter((n) => sel.noyaux.includes(n.id)).reduce((s, n) => s + (n.poids || 0) * n.prix, 0);
  const nbSel = sel.grignons.length + sel.noyaux.length;

  const generer = async () => {
    const { error, row } = await facturerVoyagesGroupes({ ...f, grignons: sel.grignons, noyaux: sel.noyaux });
    if (error) return toast.error(error);
    toast.success(`Facture groupée ${row.numero} — ${nbSel} voyage(s), ${fmt(row.total, 3)} TND`);
    setSel({ grignons: [], noyaux: [] });
    setApercu(row);
    load();
    onDone?.();
  };

  const bloc = (titre, cle, rows, colonnes) => (
    <div className="overflow-x-auto">
      <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
      <table className="w-full border-collapse text-left" data-testid={`table-voyages-${cle}`}>
        <thead>
          <tr className="border-b border-slate-200">
            {['', ...colonnes].map((c, i) => (
              <th key={i} className={th}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr data-testid={`voyages-${cle}-empty`}>
              <td colSpan={colonnes.length + 1} className="px-3 py-8 text-center text-sm text-slate-500">
                Aucun voyage en attente de facturation.
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const q = cle === 'grignons' ? r.tonnage || 0 : r.poids || 0;
              return (
                <tr key={r.id} data-testid={`voyage-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`cocher-${r.id}`}
                      checked={sel[cle].includes(r.id)}
                      onChange={() => cocher(cle, r)}
                      className="h-5 w-5 accent-slate-900"
                    />
                  </td>
                  <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{r.id}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date}</td>
                  <td className="px-3 py-2 text-sm text-slate-800">{r.acheteur}</td>
                  <td className="px-3 py-2 text-sm text-slate-700">{r.transporteur}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(q, 1)}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(r.prix, 3)}</td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(q * r.prix, 3)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="facturation-sous-produits" className="space-y-6">
      <section className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            <FileStack size={16} /> Facturation des sous-produits
          </h2>
          <p className="text-xs text-slate-500">
            Cochez plusieurs voyages d'un même client pour les fusionner en une facture consolidée
            (paiement différé — créance client).
          </p>
        </div>

        {bloc('Voyages grignons pesés', 'grignons', voyagesGr, ['Réf.', 'Date', 'Acheteur', 'Transporteur', 'Tonnage (T)', 'Prix (TND/T)', 'Montant (TND)'])}
        {bloc('Lots de noyaux non facturés', 'noyaux', voyagesNo, ['Réf.', 'Date', 'Acheteur', 'Transporteur', 'Poids (T)', 'Prix (TND/T)', 'Montant (TND)'])}

        <div className="grid grid-cols-1 gap-4 border-t border-slate-200 px-4 py-4 md:grid-cols-3">
          <div>
            <label className={label}>Date de facture</label>
            <input data-testid="grp-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} />
          </div>
          <div>
            <label className={label}>Client à facturer</label>
            <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="grp-client" />
          </div>
          <div>
            <label className={label}>Notes</label>
            <input data-testid="grp-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={field} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-4 py-4">
          <button
            type="button"
            data-testid="generer-facture-groupee"
            disabled={nbSel === 0}
            onClick={generer}
            className="flex h-12 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700 disabled:bg-slate-300"
          >
            <FileStack size={16} /> Générer une facture groupée
          </button>
          <p data-testid="grp-total" className="font-data text-sm text-slate-800">
            {nbSel} voyage(s) sélectionné(s) · {fmt(total, 3)} TND
          </p>
        </div>
      </section>

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Factures émises (groupées & diverses)
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-factures-libres">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['N° facture', 'Date', 'Client', 'Type', 'Lignes', 'Total (TND)', 'Règlement', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {factures.length === 0 ? (
                <tr data-testid="factures-libres-empty">
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-500">Aucune facture émise.</td>
                </tr>
              ) : (
                factures.map((x) => (
                  <tr key={x.id} data-testid={`facture-libre-${x.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{x.numero}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{x.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{x.client}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{x.type === 'groupee' ? 'Groupée (sous-produits)' : 'Diverse (services)'}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(x.lignes.length)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(x.total, 3)}</td>
                    <td className="px-3 py-2">
                      <span className="font-data text-[10px] uppercase tracking-[0.08em] bg-amber-50 px-1.5 py-0.5 text-amber-800">créance client</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`imprimer-facture-${x.id}`}
                        onClick={() => setApercu(x)}
                        className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        <Printer size={13} /> PDF
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {apercu && (
        <PrintDoc
          type={apercu.type === 'groupee' ? 'Facture groupée — sous-produits' : 'Facture diverse — services & prestations'}
          numero={apercu.numero}
          date={apercu.date}
          tiers={apercu.client}
          lignes={apercu.lignes.map((l) => [
            l.designation,
            `${fmt(l.quantite, 3)} ${l.unite} × ${fmt(l.prixUnitaire, 3)} = ${fmt(l.total, 3)} TND`,
          ])}
          totaux={[['Total facture', `${fmt(apercu.total, 3)} TND`]]}
          mentions={`Paiement différé — montant porté au solde dû du client.${apercu.notes ? ` ${apercu.notes}` : ''}`}
          onClose={() => setApercu(null)}
        />
      )}
    </div>
  );
};
